import {fr} from "date-fns/locale";
import {ChevronLeft, ChevronRight} from "lucide-react";
import * as React from "react";
import {DayPicker} from "react-day-picker";

import {buttonVariants} from "@/components/ui/button";
import {cn} from "@/lib/utils";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps): React.ReactElement {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      locale={fr}
      className={cn("p-3", className)}
      classNames={{
        months:
          "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 caption",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-l font-bold",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({variant: "outline"}),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex gap-1 m",
        head_cell:
          "text-muted-foreground rounded-md w-9 font-bold text-[0.9rem]",
        row: "flex w-full mt-2 justify-between gap-1",
        cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        button: "bg-none ",
        day: cn(
          buttonVariants({variant: "ghost"}),
          "day h-9 w-9 p-0 font-normal  bg-white hover:bg-primary rounded-3xl border-white border-2 border-solid  cursor-pointer",
        ),
        day_range_end: "day-range-end",
        day_selected:
          "selected aria-selected:outline-red-500 aria-selected:outline-2  aria-selected:outline aria-selected:opacity-100   text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: "bg-red-500 text-white today ",
        day_outside:
          "day-outside opacity-25 aria-selected:outline-red-500/50 text-muted-foreground hover:bg-red-500/25",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({className, ...props}) => (
          <ChevronLeft className={cn("h-4 w-4", className)} {...props} />
        ),
        IconRight: ({className, ...props}) => (
          <ChevronRight className={cn("h-4 w-4", className)} {...props} />
        ),
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export {Calendar};
